import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Mail, MapPin, Phone, Send } from 'lucide-react';
import { Logo } from '../components/Logo';
import { api, formatApiError } from '../lib/api';
import { APP_DOMAIN } from '../lib/brand';
import { formatSiteAddress, getPlatformDisplayName, useSiteSettings } from '../lib/useSiteSettings';
import { SitePublicFooter } from '../components/SitePublicFooter';

export default function Contact() {
    const { settings: site, platformName } = useSiteSettings();
    const siteAddress = formatSiteAddress(site);
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await api.post('/public/contact', form);
            setSent(true);
            setForm({ name: '', email: '', phone: '', subject: '', message: '' });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-page grain">
            <nav className="contact-nav glass">
                <div className="container-x contact-nav__inner">
                    <Logo
                        size="md"
                        logoUrl={site.logo_url}
                        platformName={platformName}
                    />
                    <div className="contact-nav__actions">
                        <Link to="/" className="btn btn-ghost" data-testid="contact-nav-home">Acasă</Link>
                        <Link to="/login" className="btn btn-ghost" data-testid="contact-nav-login">Login</Link>
                        <Link to="/register" className="btn btn-primary btn-sm" data-testid="contact-nav-register">Începe gratuit</Link>
                    </div>
                </div>
            </nav>

            <main className="contact-main">
                <div className="container-x contact-wrap">
                    <Link to="/" className="btn btn-ghost btn-sm contact-back" data-testid="contact-back">
                        <ArrowLeft size={16} /> Înapoi
                    </Link>

                    <div className="contact-grid">
                        <section className="contact-intro fade-in-up">
                            <div className="kicker">Contact</div>
                            <h1 className="headline contact-intro__title">
                                Scrie-ne — răspundem <em style={{ color: 'var(--amber)' }}>rapid</em>.
                            </h1>
                            <p className="contact-intro__text">
                                Ai întrebări despre planuri, demo sau integrare? Trimite-ne un mesaj și echipa {APP_DOMAIN} revine la tine.
                            </p>

                            <div className="contact-details">
                                {site.company_name && (
                                    <div className="contact-detail">
                                        <div className="contact-detail__icon contact-detail__icon--amber">
                                            <Building2 size={17} color="var(--amber)" />
                                        </div>
                                        <div>
                                            <div className="contact-detail__label">Denumire firmă</div>
                                            <div className="contact-detail__value">{site.company_name}</div>
                                        </div>
                                    </div>
                                )}
                                <div className="contact-detail">
                                    <div className="contact-detail__icon contact-detail__icon--amber">
                                        <Mail size={17} color="var(--amber)" />
                                    </div>
                                    <div>
                                        <div className="contact-detail__label">Email</div>
                                        <a href={`mailto:${site.contact_email}`} className="contact-detail__value">{site.contact_email}</a>
                                    </div>
                                </div>
                                {site.contact_phone && (
                                    <div className="contact-detail">
                                        <div className="contact-detail__icon contact-detail__icon--cyan">
                                            <Phone size={17} color="var(--neon-cyan)" />
                                        </div>
                                        <div>
                                            <div className="contact-detail__label">Telefon</div>
                                            <a href={`tel:${site.contact_phone.replace(/\s/g, '')}`} className="contact-detail__value">{site.contact_phone}</a>
                                        </div>
                                    </div>
                                )}
                                {siteAddress && (
                                    <div className="contact-detail">
                                        <div className="contact-detail__icon contact-detail__icon--teal">
                                            <MapPin size={17} color="var(--neon-teal)" />
                                        </div>
                                        <div>
                                            <div className="contact-detail__label">Adresă</div>
                                            <div className="contact-detail__value">{siteAddress}</div>
                                        </div>
                                    </div>
                                )}
                                {(site.cui || site.trade_register) && (
                                    <div className="contact-detail">
                                        <div className="contact-detail__icon contact-detail__icon--magenta">
                                            <Building2 size={17} color="var(--neon-magenta)" />
                                        </div>
                                        <div>
                                            <div className="contact-detail__label">Date firmă</div>
                                            <div className="contact-detail__value">
                                                {[site.cui && `CUI ${site.cui}`, site.trade_register && `Reg. Com. ${site.trade_register}`].filter(Boolean).join(' · ')}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        <div className="panel contact-form-panel fade-in-up" data-testid="contact-form-panel">
                            {sent ? (
                                <div className="contact-success" data-testid="contact-success">
                                    <div className="contact-success__icon">
                                        <Send size={22} color="var(--neon-teal)" />
                                    </div>
                                    <h2 className="headline" style={{ fontSize: '1.35rem', margin: '0 0 0.5rem' }}>Mesaj trimis!</h2>
                                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.1rem', maxWidth: 320 }}>
                                        Mulțumim — am primit mesajul tău și revenim cât de curând.
                                    </p>
                                    <button type="button" className="btn btn-secondary" onClick={() => setSent(false)} data-testid="contact-send-another">
                                        Trimite alt mesaj
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit}>
                                    <h2 className="headline contact-form-panel__title">Formular contact</h2>
                                    {error && (
                                        <div style={{
                                            padding: '0.75rem 1rem', marginBottom: '0.85rem', borderRadius: 8,
                                            background: 'rgba(255,46,146,0.1)', border: '1px solid rgba(255,46,146,0.35)',
                                            color: 'var(--neon-magenta)', fontSize: '0.88rem',
                                        }} data-testid="contact-error">
                                            {error}
                                        </div>
                                    )}
                                    <div className="contact-form-fields">
                                        <div className="contact-form-row contact-form-row--2">
                                            <div>
                                                <label className="label" htmlFor="contact-name">Nume</label>
                                                <input id="contact-name" className="input" required minLength={2} maxLength={120}
                                                       value={form.name} onChange={set('name')} placeholder="Nume Prenume"
                                                       data-testid="contact-name" />
                                            </div>
                                            <div>
                                                <label className="label" htmlFor="contact-email">Email</label>
                                                <input id="contact-email" className="input" type="email" required
                                                       value={form.email} onChange={set('email')} placeholder="nume@domeniu.ro"
                                                       data-testid="contact-email" />
                                            </div>
                                        </div>
                                        <div className="contact-form-row contact-form-row--2">
                                            <div>
                                                <label className="label" htmlFor="contact-phone">Telefon (opțional)</label>
                                                <input id="contact-phone" className="input" type="tel"
                                                       value={form.phone} onChange={set('phone')} placeholder="07xx xxx xxx"
                                                       data-testid="contact-phone" />
                                            </div>
                                            <div>
                                                <label className="label" htmlFor="contact-subject">Subiect (opțional)</label>
                                                <input id="contact-subject" className="input" maxLength={200}
                                                       value={form.subject} onChange={set('subject')} placeholder="Plan Pro"
                                                       data-testid="contact-subject" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="label" htmlFor="contact-message">Mesaj</label>
                                            <textarea id="contact-message" className="input" required minLength={10} maxLength={5000}
                                                      rows={4} value={form.message} onChange={set('message')}
                                                      placeholder="Spune-ne cum te putem ajuta..."
                                                      data-testid="contact-message" />
                                        </div>
                                    </div>
                                    <button type="submit" className="btn btn-primary contact-form-submit"
                                            disabled={loading || !form.name || !form.email || form.message.length < 10}
                                            data-testid="contact-submit">
                                        {loading ? <span className="spinner" /> : <>Trimite mesajul <ArrowRight size={16} /></>}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <SitePublicFooter site={site} showNav={false} />
        </div>
    );
}
